import * as _ from 'lodash';
import * as hash from 'object-hash';
import { Component } from 'react';
import { Dispatch, connect } from "react-redux";
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, HelpBlock, Modal, Row, Well } from 'react-bootstrap';
import { IBaseResponse, IDonorProfileAddress, IDonorProfileAddressLookupData, IDonorProfileHeaderInfo, IDonorProfileHlaInfoData, IDonorProfileHlaInfoLookupData, IDonorProfileHlaInfoPostData } from '@nkr/models/index';
import { IApplicationState, AppActionCreators, IAppState, DonorActionCreators, IDonorProfileHlaInfoLookupDataResponse, IDonorProfileHlaInfoResponse, IDonorProfileHlaInfoPostDataResponse } from '@nkr/store/index';
import { toast } from 'react-toastify';
import { IBaseDispatchProps, ToggleType } from '../models';
import HLAClass1 from './hla-class-1';
import HLAClass2 from './hla-class-2';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';

interface IOwnProps {
    centerId: number,
    activeKey: string,
    isAdmin: boolean,
    donorId: number,
    headerInfo: IDonorProfileHeaderInfo,
    hasDataChanged: boolean,
    saveRequested: boolean,
    refreshRequested: boolean,
    onDataChanged: (hasDataChanged: boolean) => void,
    onSaveFinished: (refreshHeader: boolean) => void,
    onRefreshFinished: () => void,
    onResetDataChangedWarning: () => void,
    onTabClick: (href: string) => void,
    onUpdateForErrors: (toggleType: ToggleType, messages: string[]) => void
}

interface IStateProps {
    app: IAppState,
    groupIsSuper: boolean
}

interface IDispatchProps extends IBaseDispatchProps {
    getHlaInfoData: (id: number, centerId: number) => Promise<IDonorProfileHlaInfoResponse>,
    postProfileHlaInfo: (donorId: number, centerId: number, data: IDonorProfileHlaInfoPostData) => Promise<IDonorProfileHlaInfoPostDataResponse>
    getHlaInfoLookupData: (centerId: number) => Promise<IDonorProfileHlaInfoLookupDataResponse>,
    postAutoPopulateHLA: (donorId: number, centerId: number) => Promise<IBaseResponse>,
    postConfirmHLADiscrepancy: (donorId: number, centerId: number, initials: string) => Promise<IBaseResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    data: IDonorProfileHlaInfoData,
    lookupData: IDonorProfileHlaInfoLookupData,
    addressData: IDonorProfileAddress,
    addressLookupData: IDonorProfileAddressLookupData,
    donorDataHash: string,
    showModal: boolean,
    showHLADiscrepancyModal: boolean,
    showDuplicateRecord: boolean,
    duplicateMessage: string,
    showSavedAlert: boolean,
    hasAllele: boolean,
    originalLabReviewedAntigensDate: string,
    labReviewInitials: string,
    missingInitials: boolean,
    invalidReviewDate: boolean,
    coordInitials: string,
    isEarlierThanLatestReview: boolean,
    isFutureDate: boolean,
    dirty: boolean
}

const initialState: IState = {
    data: null,
    lookupData: null,
    addressData: null,
    addressLookupData: {
        stateList: [],
        countryList: []
    },
    donorDataHash: '',
    showModal: false,
    showHLADiscrepancyModal: false,
    showDuplicateRecord: false,
    duplicateMessage: '',
    showSavedAlert: false,
    hasAllele: false,
    originalLabReviewedAntigensDate: '',
    labReviewInitials: '',
    missingInitials: false,
    invalidReviewDate: false,
    coordInitials: '',
    isEarlierThanLatestReview: false,
    isFutureDate: false,
    dirty: false
}

class HlaInfoTab extends Component<TProps, IState> {
    readonly state: IState = initialState;

    componentDidMount = () => {
        this.getData();
    }

    componentWillReceiveProps = (nextProps: Readonly<TProps>, nextContext: any) => {
        if (this.props.saveRequested == false && nextProps.saveRequested == true) {
            this.handleSave();
        }

        if (this.props.refreshRequested == false && nextProps.refreshRequested == true) {
            this.getData();
        }
    }

    getData = () => {
        this.props.showLoading('Getting hla data, one moment please...').then(() => {
            Promise.all([
                this.props.getHlaInfoData(this.props.centerId, this.props.donorId),
                this.props.getHlaInfoLookupData(this.props.centerId)
            ])
                .then(([dataResponse, lookupDataResponse]) => {
                    if (dataResponse.data.success && lookupDataResponse.data.success) {
                        let donorDataHash = hash(dataResponse.data.results);

                        this.setState({
                            donorDataHash,
                            data: dataResponse.data.results,
                            lookupData: lookupDataResponse.data.results,
                            hasAllele: dataResponse.data.results.hasAlleleData,
                            originalLabReviewedAntigensDate: dataResponse.data.results.labReviewDate
                        });
                    }
                })
                .catch(error => {

                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    refreshData = () => {
        this.props.getHlaInfoData(this.props.centerId, this.props.donorId).then(response => {
            if (response.data.success) {
                let donorDataHash = hash(response.data.results);

                this.setState({
                    donorDataHash,
                    data: response.data.results,
                    hasAllele: response.data.results.hasAlleleData,
                    originalLabReviewedAntigensDate: response.data.results.labReviewDate,
                    dirty: false,
                    missingInitials: false,
                    invalidReviewDate: false,
                    labReviewInitials: ''
                }, () => {
                    this.props.onDataChanged(false);
                    this.props.onResetDataChangedWarning();
                    this.props.onUpdateForErrors(ToggleType.Success, []);
                    this.props.onSaveFinished(true);
                });
            }
        });
    }

    handleAutoPopulateHLA = () => {
        let { centerId, donorId } = this.props;

        this.setState({ showModal: false });

        this.props.showLoading('HLA Auto Populating, one moment please...').then(() => {
            this.props.postAutoPopulateHLA(donorId, centerId)
                .then(response => {
                    if (response.data.success) {
                        this.getData();
                    }
                })
                .catch(error => {
                    if (this.handleDuplicateError(error)) {
                        return;
                    }

                    toast.error(error.response.data, { autoClose: 5000 });
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleDuplicateError = (error: any): boolean => {
        if (error.response.status != 409) {
            return false;
        }

        this.setState({
            showDuplicateRecord: true,
            duplicateMessage: error.response.data
        }, () => {
            this.props.onSaveFinished(false);
        });

        return true;
    }

    handleCloseDuplicateRecord = () => {
        this.setState({
            showDuplicateRecord: false
        });
    }

    handleDataChange = (data: IDonorProfileHlaInfoData) => {
        let newData = _.cloneDeep(data);
        let newHash = hash(newData);

        let hasDataChanged = newHash != this.state.donorDataHash;

        this.setState({
            data: newData,
        });

        this.props.onDataChanged(hasDataChanged);
    }

    handleInitialsChanged = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;
        let alphaValue = value.replace(/[^A-Za-z]/g, '');

        this.setState({
            coordInitials: alphaValue.toUpperCase()
        });
    }

    handleSave = () => {
        let { centerId, donorId } = this.props;
        let { data } = this.state

        let { originalLabReviewedAntigensDate, labReviewInitials } = this.state;

        let selectedDate = new Date(data.labReviewDate);
        let originalDate = new Date(originalLabReviewedAntigensDate);

        let today = new Date();
        today.setHours(0, 0, 0, 0);

        let isEarlierThanLatestReview = selectedDate < originalDate;
        let isFutureDate = selectedDate > today;

        let dateInvalid = isEarlierThanLatestReview || isFutureDate;
        let dateChanged = data.labReviewDate !== originalLabReviewedAntigensDate;
        let initialsMissing = labReviewInitials == '';


        if (dateChanged && (initialsMissing || dateInvalid)) {
            this.setState({
                missingInitials: initialsMissing,
                invalidReviewDate: dateInvalid,
                isEarlierThanLatestReview,
                isFutureDate,
                dirty: true
            }, () => {
                this.props.onSaveFinished(false);

                if (initialsMissing) {
                    this.props.onUpdateForErrors(
                        ToggleType.Errors,
                        ['Please specify your initials']
                    );
                }

                if (dateInvalid) {
                    this.props.onUpdateForErrors(
                        ToggleType.Errors,
                        ['Review Date cannot be earlier than the current Review Date']
                    );
                }
            });
        } else {
            let postData: IDonorProfileHlaInfoPostData = {
                hlaA1: data.hlaA1,
                hlaA2: data.hlaA2,
                hlaBw1: data.hlaBw1,
                hlaBw2: data.hlaBw2,
                hlaB1: data.hlaB1,
                hlaB2: data.hlaB2,
                hlaC1: data.hlaC1,
                hlaC2: data.hlaC2,
                hlaDrb1: data.hlaDrb1,
                hlaDrb2: data.hlaDrb2,
                hlaDqb1: data.hlaDqb1,
                hlaDqb2: data.hlaDqb2,
                hlaDpb1: data.hlaDpb1,
                hlaDpb2: data.hlaDpb2,
                hlaDqa1: data.hlaDqa1,
                hlaDqa2: data.hlaDqa2,
                hlaDpa1: data.hlaDpa1,
                hlaDpa2: data.hlaDpa2,
                hlaHasDr51: data.hlaHasDr51,
                hlaHasDr52: data.hlaHasDr52,
                hlaHasDr53: data.hlaHasDr53,
                labReviewComplete: data.labReviewComplete,
                labReviewDate: data.labReviewDate,
                labReviewInitials: this.state.labReviewInitials
            }

            this.props.showLoading('Saving hla data, one moment please...').then(() => {
                this.props.postProfileHlaInfo(donorId, centerId, postData)
                    .then(response => {
                        if (response.data.success) {
                            // let donorDataHash = hash(data);

                            // this.setState({
                            //     donorDataHash,
                            //     missingInitials: false,
                            //     dirty: false
                            // }, () => {
                            //     this.props.onDataChanged(false);
                            //     this.props.onResetDataChangedWarning();
                            //     this.props.onUpdateForErrors(ToggleType.Success, []);
                            //     this.props.onSaveFinished();
                            // });
                            this.refreshData();
                        }
                    })
                    .catch(error => {
                        if (this.handleDuplicateError(error)) {
                            return;
                        }

                        toast.error(error.response.data, { autoClose: 5000 });
                        this.props.onSaveFinished(false);
                    })
                    .finally(() => {
                        this.props.hideLoading();
                    });
            });
        }
    }

    handleHLADiscrepancy = () => {
        let { centerId, donorId } = this.props;
        let { coordInitials } = this.state;

        this.setState({ showHLADiscrepancyModal: false });

        this.props.showLoading('Updating HLA record, one moment please...').then(() => {
            this.props.postConfirmHLADiscrepancy(donorId, centerId, coordInitials)
                .then(response => {
                    if (response.data.success) {
                        this.refreshData();
                    }
                })
                .catch(error => {
                    toast.error(error.response.data, { autoClose: 5000 });
                })
                .finally(() => {
                    this.props.hideLoading();
                    this.setState({ showSavedAlert: true });
                });
        });
    }

    handleShowingModal = () => {
        this.setState(prevstate => ({
            showModal: !prevstate.showModal
        }))
    }

    handleShowingHLAModal = () => {
        this.setState(prevstate => ({
            showHLADiscrepancyModal: !prevstate.showHLADiscrepancyModal,
            coordInitials: ''
        }))
    }

    handleInitialsChange = (labReviewInitials: string) => {
        this.setState({
            labReviewInitials
        });
    }

    checkValid = (): boolean => {
        return this.state.coordInitials.trim().length > 1;
    }


    render = () => {
        let { data, lookupData, hasAllele } = this.state;
        let disableSubmit = !this.checkValid();

        if (data == null || lookupData == null || this.props.headerInfo == null)
            return null;

        return (
            <>
                <div className='container-fluid donor-profile-section'>
                    <Row>
                        <Col xs={12}>
                            <Well style={{ paddingTop: 9.5, paddingBottom: 9.5 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                    }}
                                >
                                    {/* Left side text */}
                                    <div style={{ flex: '1 1 auto', minWidth: '250px' }}>
                                        <FormControl.Static>
                                            All antigen information must be complete to be exportable
                                        </FormControl.Static>
                                    </div>

                                    {/* Button */}
                                    <div style={{ flex: '0 0 auto' }}>
                                        <Button
                                            className="wide_button"
                                            disabled={!hasAllele}
                                            onClick={this.handleShowingModal}
                                        >
                                            Auto-Populate HLA
                                        </Button>
                                    </div>

                                    {/* Legend section */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flex: '1 1 auto',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            fontSize: 12,
                                            gap: '10px',
                                        }}
                                    >


                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    backgroundColor: '#5bb85d',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <div>- Matched Antigen</div>
                                        </div>




                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    backgroundColor: '#FF954D',
                                                    border: '1px solid #FF954D',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <div>- Unexpected Antigen (overridden)</div>
                                        </div>



                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    backgroundColor: '#C83333',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <div>- Unexpected Antigen</div>
                                        </div>


                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    backgroundColor: '#777777',
                                                    border: '1px solid #bbb',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <div>- Not Evaluated</div>
                                        </div>
                                    </div>
                                </div>
                            </Well>
                        </Col>
                    </Row>


                    {this.state.data.hasHlaDiscrepancy &&
                        // !this.state.data.hlaDonorInfo.hasHlaMissingErrors &&
                        !this.state.data.hlaDiscrepancyIsEquivalent && (
                            <Row>
                                <Col xs={12}>
                                    <Alert bsSize="large" style={{
                                        marginBottom: 5,
                                        backgroundColor: '#f8d7da',
                                        borderColor: '#f5c6cb',
                                        color: 'black',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap'
                                    }}>
                                        <p style={{ margin: 0, flex: 1 }}>
                                            <span style={{ fontSize: 17, fontWeight: 'bold', color: '#d35a52' }}>Alert:</span> We found a discrepancy in the serological equivalents determined from our high resolution uploads versus what you entered below. This could be an error or could result from an equivalent serological group. Please ensure that all HLA is reviewed and then confirm this data is correct.
                                        </p>
                                        <Button bsStyle="danger" bsSize="sm" disabled={!hasAllele} onClick={this.handleShowingHLAModal} style={{ marginLeft: 10, whiteSpace: 'nowrap' }}>
                                            <div style={{ fontSize: 14 }}>Confirm Equivalent Serological Group</div>
                                        </Button>
                                    </Alert>
                                </Col>
                            </Row>)
                    }

                    {this.state.showSavedAlert && (
                        <Row>
                            <Col xs={12}>
                                <Alert bsSize="large" style={{
                                    marginBottom: 5,
                                    backgroundColor: '#def0da',
                                    borderColor: '#9bc49b',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap'
                                }}>
                                    <p style={{ margin: 0, flex: 1 }}>
                                        <span style={{ fontSize: 17, fontWeight: 'bold', color: '#337034ff' }}>Saved:</span> HLA override confirmed and saved successfully
                                    </p>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <Row style={{ display: 'flex', alignItems: 'stretch' }}>
                        <HLAClass1
                            data={data}
                            labReviewInitials={this.state.labReviewInitials}
                            lookupData={lookupData}
                            missingInitials={this.state.missingInitials}
                            invalidReviewDate={this.state.invalidReviewDate}
                            isEarlierThanLatestReview={this.state.isEarlierThanLatestReview}
                            isFutureDate={this.state.isFutureDate}
                            dirty={this.state.dirty}
                            onDataChange={this.handleDataChange}
                            onInitialsChange={this.handleInitialsChange}
                        />

                        <HLAClass2
                            data={data}
                            lookupData={lookupData}
                            onDataChange={this.handleDataChange}
                        />
                    </Row>
                </div>

                <Modal show={this.state.showModal} onHide={this.handleShowingModal}>
                    <Modal.Header closeButton>
                        Please Confirm
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to overwrite any HLA values below?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button bsStyle='primary' onClick={this.handleAutoPopulateHLA}>Yes</Button>
                        <Button bsStyle='secondary' onClick={this.handleShowingModal}>No</Button>
                    </Modal.Footer>
                </Modal>


                <Modal show={this.state.showHLADiscrepancyModal} bsSize='lg' backdrop='static' onHide={this.handleShowingHLAModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm HLA Discrepancy</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            Please ensure that you have verified each HLA serological antigen
                            Once confirmed, if this error persists, it's likely due to an equivalent serological group creating a false flag, in which case you may sign off below.
                        </p>

                        <Alert style={{ background: '#e9ecef', borderColor: '#ced4da', color: 'black' }}>
                            I confirm that the HLA antigens listed are correct and that the discrepancy is a false flag likely due to an equivalent serological group.

                            <FormGroup style={{ marginTop: '0.8em', marginBottom: 0, marginRight: '0.5em' }}>
                                <ControlLabel>Authorizing Coordinator Initials</ControlLabel>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                    <FormControl
                                        type="text"
                                        value={this.state.coordInitials}
                                        maxLength={3}
                                        placeholder="Coordinator Initials"
                                        bsSize='sm'
                                        onChange={this.handleInitialsChanged}
                                        style={{ width: '25%' }}
                                    />

                                    <Button
                                        bsStyle="danger"
                                        disabled={disableSubmit}
                                        onClick={this.handleHLADiscrepancy}
                                    >
                                        Confirm False Flag
                                    </Button>
                                </div>

                                <HelpBlock>At least two characters required.</HelpBlock>
                            </FormGroup>
                        </Alert>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button onClick={this.handleShowingHLAModal}>Cancel</Button>
                    </Modal.Footer>
                </Modal>

                <DuplicateRecordModal
                    show={this.state.showDuplicateRecord}
                    message={this.state.duplicateMessage}
                    onClose={this.handleCloseDuplicateRecord}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getHlaInfoData: (centerId, id) => {
            return dispatch(DonorActionCreators.getProfileHlaInfo(id, centerId));
        },
        postProfileHlaInfo: (donorId, centerId, data) => {
            return dispatch(DonorActionCreators.postProfileHlaInfo(donorId, centerId, data));
        },
        getHlaInfoLookupData: (centerId) => {
            return dispatch(DonorActionCreators.getProfileHlaInfoLookupData(centerId));
        },
        postAutoPopulateHLA: (donorId, centerId) => {
            return dispatch(DonorActionCreators.postAutoPopulateHLA(donorId, centerId))
        },
        postConfirmHLADiscrepancy: (donorId, centerId, initials) => {
            return dispatch(DonorActionCreators.postConfirmHLADiscrepancy(donorId, centerId, initials))
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(HlaInfoTab);